import { useEffect, useMemo, useRef } from 'react';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Heading,
  HStack,
  IconButton,
  SimpleGrid,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { IoArrowBack } from 'react-icons/io5';
import { Link as RouterLink, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';

import {
  buildComparePath,
  compareEventProps,
  DATASETS_PARAM,
  datasetRefKey,
  encodeCompareDatasets,
  isSameSystemModel,
  parseCompareDatasets,
  useIsDatasetComparisonEnabled,
} from 'components/shared/Compare';
import PageHead from 'components/shared/PageHead';
import AppCard from 'components/UI/AppCard';
import useVersionInfo from 'hooks/useVersionInfo';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import ComparisonCard from './ComparisonCard';
import { CrossReleaseNotice, describeReleases } from './CrossReleaseNotice';
import ExchangesComparison from './ExchangesComparison';
import ImpactAssessmentComparison from './ImpactAssessmentComparison';
import { useResolveCompareDataset } from './useResolveCompareDataset';

const COMPARE_TABS = [
  { slug: 'impact-assessment', label: 'Impact Assessment' },
  { slug: 'exchanges', label: 'Exchanges' },
] as const;

/**
 * The Exchanges tab was `flows` until the feature was aligned with V3's wording (V4 is the half of
 * the app that says "flows"). Links to the old slug are already out in tickets and PR descriptions,
 * so send them to the renamed tab rather than letting them fall back to the default one.
 */
const LEGACY_TAB_SLUGS: Record<string, string> = { flows: 'exchanges' };

const Comparison = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ver, system_model } = useVersionInfo();
  const [searchParams] = useSearchParams();

  // Matches the `grayText` used across the comparison tabs, so the back arrow reads as secondary.
  const backArrowColor = useColorModeValue('gray.600', 'gray.400');

  const routeRelease = useMemo(() => ({ version: ver, system_model }), [ver, system_model]);
  const rawDatasets = searchParams.get(DATASETS_PARAM);

  // Each dataset carries its own release, so nothing on this page derives a release from the route.
  const { refs, invalidEntries, droppedEntries } = useMemo(
    () => parseCompareDatasets(rawDatasets, routeRelease),
    [rawDatasets, routeRelease]
  );

  const searchPath = `/${ver}/${system_model}/search`;
  const comparePath = `/${ver}/${system_model}/compare`;

  const currentSlug = location.pathname.split('/')[4];
  const activeIndex = COMPARE_TABS.findIndex((tab) => tab.slug === currentSlug);
  const isCompareEnabled = useIsDatasetComparisonEnabled();

  const [refA, refB] = [refs[0], refs[1]];

  // Resolved here so the view event can describe what is being compared; react-query dedupes these
  // with the cards, so it costs no extra requests.
  const { dataset: datasetA, isLoading: isLoadingA } = useResolveCompareDataset(refA);
  const { dataset: datasetB, isLoading: isLoadingB } = useResolveCompareDataset(refB);
  const isFromTray = Boolean((location.state as { fromTray?: boolean } | null)?.fromTray);

  // Cutoff vs consequential is not a sound comparison, so no numbers are shown for it. The tray
  // refuses it up front; this catches shared links, which bypass the tray entirely.
  const hasMixedSystemModels = Boolean(refA && refB && !isSameSystemModel(refA, refB));

  // The route's release segment only drives the breadcrumb, the sidebar and the global Mixpanel
  // super-property — so canonicalise it to the first dataset's release, otherwise it can describe
  // neither of them.
  const shouldCanonicaliseRoute =
    Boolean(refA) && (refA.version !== ver || refA.systemModel !== system_model) && activeIndex !== -1;

  useEffect(() => {
    if (!isCompareEnabled) return;

    if (shouldCanonicaliseRoute) {
      navigate(buildComparePath(currentSlug, { version: refA.version, system_model: refA.systemModel }, refs), {
        replace: true,
        state: location.state,
      });
      return;
    }

    if (activeIndex === -1) {
      // Keep location.state so the view event can still tell a tray launch from a shared link.
      navigate(`${comparePath}/${LEGACY_TAB_SLUGS[currentSlug] ?? COMPARE_TABS[0].slug}${location.search}`, {
        replace: true,
        state: location.state,
      });
    }
  }, [
    isCompareEnabled,
    shouldCanonicaliseRoute,
    activeIndex,
    navigate,
    currentSlug,
    refA,
    refs,
    comparePath,
    location.search,
    location.state,
  ]);

  // One view event per comparison, fired once the datasets have settled so the same_* props are filled.
  const trackedViewKeyRef = useRef<string | null>(null);
  // Keyed on the encoded refs, not the ids: changing only a release must still count as a new view.
  const viewKey = encodeCompareDatasets(refs);

  useEffect(() => {
    if (!isCompareEnabled || !viewKey || activeIndex === -1) return;
    if (isLoadingA || isLoadingB) return;
    if (trackedViewKeyRef.current === viewKey) return;

    trackedViewKeyRef.current = viewKey;
    mixpanel.track(MIXPANEL_EVENTS.COMPARISON_VIEWED, {
      ...compareEventProps([datasetA, datasetB]),
      // The URL is the source of truth for what was requested, even if a dataset failed to load.
      dataset_ids: refs.map((ref) => ref.id),
      dataset_count: refs.length,
      dataset_releases: refs.map((ref) => `${ref.version}-${ref.systemModel}`),
      tab: currentSlug,
      entry: isFromTray ? 'tray' : 'direct_link',
    });
  }, [isCompareEnabled, viewKey, refs, activeIndex, isLoadingA, isLoadingB, datasetA, datasetB, currentSlug, isFromTray]);

  useEffect(() => {
    if (!hasMixedSystemModels) return;
    mixpanel.track(MIXPANEL_EVENTS.COMPARISON_BLOCKED, {
      reason: 'system_model_mismatch',
      dataset_ids: refs.map((ref) => ref.id),
      dataset_releases: refs.map((ref) => `${ref.version}-${ref.systemModel}`),
    });
  }, [hasMixedSystemModels, refs]);

  const onTabClick = (slug: string) => {
    if (slug === currentSlug) return;
    mixpanel.track(MIXPANEL_EVENTS.COMPARISON_TAB_CHANGED, {
      tab: slug,
      from_tab: currentSlug,
      dataset_ids: refs.map((ref) => ref.id),
    });
  };

  // The feature is off in production and for v4 releases — send anyone with a stale link back to search.
  if (!isCompareEnabled) return <Navigate to={searchPath} replace />;

  return (
    <Stack alignItems={'center'} width="100%" spacing={0}>
      <PageHead title="Dataset comparison" />
      <Stack maxWidth={'container.xl'} width={{ base: '100%', md: '95%' }} spacing={4} py={5}>
        <Stack spacing={4} px={{ base: 4, md: 0 }}>
          <Breadcrumb fontSize="sm">
            <BreadcrumbItem>
              <BreadcrumbLink as={RouterLink} to={searchPath}>
                Datasets
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage fontWeight="medium">
              <BreadcrumbLink>Dataset comparison</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
          <Heading as="h4">Dataset comparison</Heading>
        </Stack>

        <AppCard spacing={6}>
          <HStack spacing={3}>
            <IconButton
              aria-label="Back"
              icon={<IoArrowBack />}
              variant="link"
              fontSize="24px"
              color={backArrowColor}
              minW="auto"
              onClick={() => navigate(-1)}
            />
            <Heading size="lg">Comparison</Heading>
          </HStack>

          {invalidEntries.length > 0 && (
            <Alert status="warning" variant="left-accent" borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                {invalidEntries.length === 1 ? 'One dataset in this link' : `${invalidEntries.length} datasets in this link`}{' '}
                could not be read and {invalidEntries.length === 1 ? 'was' : 'were'} skipped. A dataset must be identified by
                its id and release.
              </AlertDescription>
            </Alert>
          )}

          {droppedEntries.length > 0 && (
            <Alert status="info" variant="left-accent" borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                Only the first two datasets are compared; {droppedEntries.length} more{' '}
                {droppedEntries.length === 1 ? 'was' : 'were'} ignored.
              </AlertDescription>
            </Alert>
          )}

          {refs.length === 0 ? (
            <Text color="gray.500">
              No datasets selected to compare.{' '}
              <BreadcrumbLink as={RouterLink} to={searchPath} color="green.500">
                Go back to search
              </BreadcrumbLink>
            </Text>
          ) : (
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
              {refs.map((ref) => (
                <ComparisonCard key={datasetRefKey(ref)} datasetRef={ref} />
              ))}
            </SimpleGrid>
          )}

          {hasMixedSystemModels ? (
            <CrossReleaseNotice title="These datasets can't be compared">
              {describeReleases([refA, refB])} use different system models. Cutoff and consequential answer different questions,
              so their scores aren&apos;t directly comparable. Pick two datasets that share a system model.
            </CrossReleaseNotice>
          ) : (
            <Tabs variant="soft-rounded" colorScheme="green" index={Math.max(activeIndex, 0)} isLazy>
              <TabList>
                {COMPARE_TABS.map((tab) => (
                  <Tab
                    key={tab.slug}
                    as={RouterLink}
                    // soft-rounded is a full pill by default; the design wants a softer corner, and
                    // this is set here rather than on the theme so other soft-rounded tabs are left alone.
                    borderRadius="md"
                    to={`${comparePath}/${tab.slug}${location.search}`}
                    onClick={() => onTabClick(tab.slug)}
                  >
                    {tab.label}
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
                  <ImpactAssessmentComparison refs={refs} />
                </TabPanel>
                <TabPanel px={0}>
                  <ExchangesComparison refs={refs} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </AppCard>
      </Stack>
    </Stack>
  );
};

export default Comparison;
